/**
 * Glassmorphism HTML nameplate overlay.
 *
 *  Phaser text labels render flat. Real apps in 2026 use frosted-glass HTML
 *  panels with backdrop-blur. We position one HTML div per player above the
 *  character canvas, syncing position each frame from Phaser camera + body.
 *
 *  Architecture:
 *   - One <div> per visible player, stored in nameplate-container
 *   - Each frame, MainScene calls syncTo(sessionId, name, x, y, mood, halo)
 *     and we compute screen position via cameraToScreen() and css transform.
 *   - When a player leaves, MainScene calls remove(sessionId).
 */

export interface NameplateOptions {
  name: string;
  mood: string;        // normal / tired / frustrated / irate
  haloHex: string;     // accent colour for status dot
  presence?: "online" | "idle" | "offline";
  workingDir?: string;
}

const MOOD_TO_EMOJI: Record<string, string> = {
  normal: "",
  tired: "😴",
  frustrated: "😤",
  irate: "🔥",
};

const PRESENCE_DOT: Record<string, string> = {
  online: "#4ade80",
  idle: "#fbbf24",
  offline: "#6b7280",
};

export class Nameplates {
  private container: HTMLDivElement;
  private plates = new Map<string, HTMLDivElement>();

  constructor() {
    const c = document.createElement("div");
    c.id = "nameplate-container";
    c.style.cssText = `
      position: fixed; inset: 0;
      pointer-events: none;
      z-index: 4;
      overflow: hidden;
    `;
    document.body.appendChild(c);
    this.container = c;
  }

  /** Update or create a nameplate for a player. */
  syncTo(
    id: string,
    screenX: number, screenY: number,
    opts: NameplateOptions,
  ) {
    let plate = this.plates.get(id);
    if (!plate) {
      plate = this.createPlate();
      this.plates.set(id, plate);
      this.container.appendChild(plate);
    }
    this.renderContent(plate, opts);
    // Position above head; transform-based for sub-pixel accuracy.
    plate.style.transform = `translate3d(${screenX}px, ${screenY}px, 0) translate(-50%, -100%)`;
  }

  remove(id: string) {
    const plate = this.plates.get(id);
    if (!plate) return;
    plate.remove();
    this.plates.delete(id);
  }

  destroy() {
    this.container.remove();
    this.plates.clear();
  }

  private createPlate(): HTMLDivElement {
    const div = document.createElement("div");
    div.style.cssText = `
      position: absolute;
      top: 0; left: 0;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 10px 4px 8px;
      border-radius: 999px;
      font-family: Inter, system-ui, sans-serif;
      font-size: 11px;
      font-weight: 600;
      color: #f1f5f9;
      background: rgba(15, 23, 42, 0.55);
      backdrop-filter: blur(8px) saturate(140%);
      -webkit-backdrop-filter: blur(8px) saturate(140%);
      border: 1px solid rgba(255, 255, 255, 0.18);
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.04) inset;
      white-space: nowrap;
      transition: background 0.4s, color 0.4s;
      will-change: transform;
    `;
    return div;
  }

  private renderContent(plate: HTMLDivElement, opts: NameplateOptions) {
    // Use textContent to avoid XSS — never inject HTML.
    while (plate.firstChild) plate.removeChild(plate.firstChild);

    // Status dot
    const dot = document.createElement("span");
    const presence = opts.presence ?? "online";
    dot.style.cssText = `
      width: 7px; height: 7px;
      border-radius: 50%;
      background: ${PRESENCE_DOT[presence]};
      box-shadow: 0 0 6px ${PRESENCE_DOT[presence]};
      flex-shrink: 0;
    `;
    plate.appendChild(dot);

    // Name
    const name = document.createElement("span");
    name.textContent = opts.name;
    plate.appendChild(name);

    // Mood emoji (only if non-normal)
    const moodEmoji = MOOD_TO_EMOJI[opts.mood];
    if (moodEmoji) {
      const moodSpan = document.createElement("span");
      moodSpan.textContent = moodEmoji;
      moodSpan.style.fontSize = "10px";
      plate.appendChild(moodSpan);
    }

    // Tier accent stripe (left edge highlight)
    plate.style.borderLeft = `2px solid ${opts.haloHex}`;
    // Mood-tinted background subtle effect
    if (opts.mood === "irate") {
      plate.style.background = "rgba(127, 29, 29, 0.55)";
    } else if (opts.mood === "frustrated") {
      plate.style.background = "rgba(120, 53, 15, 0.55)";
    } else if (opts.mood === "tired") {
      plate.style.background = "rgba(78, 67, 14, 0.55)";
    } else {
      plate.style.background = "rgba(15, 23, 42, 0.55)";
    }
  }
}
